// Attorney Brian prompt library — shown on Training → Prompts. Copy a prompt,
// paste it into your Claude chat that has the Attorney Brian connector, replace
// the [brackets]. Keep these aligned with the MCP tools in functions/api/mcp.
export interface BrianPrompt {
  title: string;
  when: string;      // one line: when to use it
  prompt: string;    // the text to copy
  tip?: string;      // optional coaching
}
export interface BrianPromptGroup {
  id: string;
  title: string;
  blurb: string;
  prompts: BrianPrompt[];
}

export const BRIAN_PROMPT_GROUPS: BrianPromptGroup[] = [
  {
    id: "start",
    title: "Start of a case",
    blurb: "Brian works one client at a time. Always name the client, and let him look before he acts.",
    prompts: [
      {
        title: "Where does this client stand?",
        when: "First thing you ask on any client, every time.",
        prompt: "Pull up [client full name]. Give me their status, whether the service agreement is signed, the latest report on file, any open dispute rounds and letters, and what you recommend as the next step. Do not draft anything yet.",
        tip: "If Brian says he cannot find the client, they are not in your company's system under that name. Check spelling in Clients first.",
      },
      {
        title: "Analyze the latest report",
        when: "After a report has been imported and you want the findings explained.",
        prompt: "Run the analysis on the latest credit report for [client full name]. Group the findings by bureau, tell me the severity and the FCRA basis for each, and flag anything that is technically accurate so we do not waste a round on it.",
      },
      {
        title: "Which items are worth disputing?",
        when: "Before you pick items for a round.",
        prompt: "For [client full name], from the current analysis, which items should go in round one and which should wait? Explain each pick in one sentence. Do not include items that are accurate and current.",
        tip: "Disputing accurate items burns a 30-day cycle and teaches the bureau to ignore you.",
      },
    ],
  },
  {
    id: "rounds",
    title: "Dispute rounds and letters",
    blurb: "Brian drafts, you review, then you mail from the app. He never mails on his own.",
    prompts: [
      {
        title: "Draft round one",
        when: "You have chosen the items.",
        prompt: "Draft dispute round one for [client full name] using these findings: [list the item names or say \"everything you recommended\"]. Use the client's own voice, one letter per bureau, and tell me when the drafts are ready for my review in the app.",
      },
      {
        title: "Draft the next round after results",
        when: "The bureau responses came back and you logged them.",
        prompt: "Round [number] results are logged for [client full name]. Draft the next round targeting only what survived. For anything that came back \"verified,\" use the method-of-verification demand under FCRA 611(a)(7). Tell me what changed from the last round.",
      },
      {
        title: "Direct furnisher dispute",
        when: "A creditor or collector keeps verifying an item the bureaus will not fix.",
        prompt: "Draft a direct dispute to the furnisher under FCRA 623(a)(8) for [client full name] on [account or collector name]. Include the specific inaccuracy, the documents we are attaching, and the 30-day investigation requirement.",
      },
      {
        title: "Debt validation demand",
        when: "A collection account shows up and the client does not recognise it or the amount.",
        prompt: "Draft a debt validation demand under FDCPA 1692g for [client full name] to [collector name] about [account]. Request the original creditor, the amount and how it was calculated, proof they are licensed to collect in [state], and a copy of the signed agreement.",
      },
      {
        title: "Show me the letters",
        when: "You want a quick list before mailing.",
        prompt: "List every letter for [client full name] with its status, bureau or recipient, and the date. Tell me which ones are ready to mail and which are waiting on my review.",
      },
    ],
  },
  {
    id: "mail",
    title: "Mailing",
    blurb: "Mail runs on your own carrier keys. Brian needs you to say yes before anything goes out.",
    prompts: [
      {
        title: "Queue and send a round",
        when: "The drafts are reviewed and you are ready.",
        prompt: "The round [number] letters for [client full name] are approved. Queue them with [Lob / LetterStream / manual] and send. Confirm the cost and the tracking before you finish, and tell me the 30-day follow-up date.",
        tip: "There is no default carrier. If you do not name one, Brian will ask.",
      },
      {
        title: "What is due this week?",
        when: "Monday planning.",
        prompt: "Across all my clients, which bureau reinvestigation deadlines land in the next 7 days, and which clients have letters that were mailed more than 30 days ago with no response logged?",
      },
    ],
  },
  {
    id: "agencies",
    title: "FTC and CFPB filings",
    blurb: "FTC identity-theft reports need the client's own written attestation first. The CFPB is always submitted by a person, never by Brian.",
    prompts: [
      {
        title: "Prepare an FTC identity-theft report",
        when: "The client says accounts are not theirs. Only after they attest.",
        prompt: "Prepare the FTC identity-theft filing for [client full name] covering [the accounts]. Stage it for the client's review, send me the client review link so they can read and sign the attestation, and do not fill anything on IdentityTheft.gov until that attestation is recorded.",
        tip: "No attestation, no filing. That is a hard legal line, not a preference.",
      },
      {
        title: "Prepare a CFPB complaint",
        when: "A bureau ignored or rubber-stamped a dispute after 30 days.",
        prompt: "Prepare a CFPB complaint for [client full name] against [Experian / Equifax / TransUnion / the tenant screening company] about [what they failed to do]. Build the narrative and the attachment bundle from the dispute history, then give me the fill payload. I will press Submit myself.",
      },
      {
        title: "Log a submitted filing",
        when: "Right after you pressed Submit on the CFPB or FTC site.",
        prompt: "I submitted the [CFPB / FTC] filing for [client full name]. The confirmation number is [number]. Log it on the client file with today's date.",
      },
    ],
  },
  {
    id: "situations",
    title: "Special situations",
    blurb: "Copy these when a client brings you something outside the normal bureau cycle.",
    prompts: [
      {
        title: "Bad rental history / tenant screening",
        when: "A landlord denied the client and told them to \"contact the CFPB.\"",
        prompt: "[Client full name] was denied housing and the property company blamed their rental history. We do not yet know which tenant screening company reported them. Draft the FCRA 615(a) adverse-action disclosure demand to the property company so they must name the screening agency within 10 days, and list the screening companies the client should request free file disclosures from under FCRA 612.",
      },
      {
        title: "Old collection, past the reporting limit",
        when: "An item looks older than seven years from the date of first delinquency.",
        prompt: "For [client full name], the [account] shows a date of first delinquency of [date]. Confirm whether it is past the 7-year reporting limit under FCRA 605(c), explain the DOFD math, and draft the obsolete-information dispute if it is.",
      },
      {
        title: "Bankruptcy or public record",
        when: "A public record is reporting and the client wants it addressed.",
        prompt: "[Client full name] has a [bankruptcy / judgment / lien] reporting with [bureau]. Explain the furnisher-identity approach for public records, what documents we need from the client, and draft the first dispute.",
      },
      {
        title: "Hard inquiry the client did not authorise",
        when: "Unrecognised inquiries on the report.",
        prompt: "List the hard inquiries on [client full name]'s latest report. For any the client did not authorise, tell me whether they are tied to an open account (in which case we leave them alone) and draft the FCRA 604 permissible-purpose dispute for the rest.",
      },
    ],
  },
  {
    id: "client",
    title: "Talking to the client",
    blurb: "Brian can draft what you say to the client. You send it from the Messages tab or the client's file.",
    prompts: [
      {
        title: "Plain-English progress update",
        when: "The client asks \"what's happening with my case?\"",
        prompt: "Write a short progress update I can send to [client full name] in plain English: what we disputed, where each bureau is in the 30-day clock, and what happens next. No legal citations, no promises about scores, under 150 words.",
        tip: "Never promise a score or a deletion. Brian will not write one, and neither should you.",
      },
      {
        title: "Document request",
        when: "You need something from the client to move forward.",
        prompt: "Write a friendly message to [client full name] asking for [what you need, e.g. the denial letter, a copy of their ID, the collector's letter], why we need it, and the easiest way to send it (upload in their portal).",
      },
      {
        title: "Explain a bureau result",
        when: "A bureau letter came back and the client is confused.",
        prompt: "[Client full name] got a letter from [bureau] saying [what it says]. Explain what it means in two short paragraphs I can send them, and what our next move is.",
      },
    ],
  },
  {
    id: "habits",
    title: "Getting good answers from Brian",
    blurb: "Three habits that make every answer better.",
    prompts: [
      {
        title: "Ask him to check first",
        when: "Any time you are about to ask for a draft.",
        prompt: "Before you draft anything, pull the client record and the latest analysis for [client full name] and tell me what you see. Then wait for my go-ahead.",
      },
      {
        title: "Ask for the reasoning",
        when: "You want to learn, not just get output.",
        prompt: "Explain why you chose that letter type and that FCRA section for [item], in the same words you would use to teach a new analyst.",
      },
      {
        title: "Ask what he cannot do",
        when: "Brian says he cannot do something.",
        prompt: "What exactly do you need from me to finish that, and where in the app do I do it?",
        tip: "Brian only sees your company's clients through the app's tools. He has no email, no browser, and no files of his own.",
      },
    ],
  },
];
